import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  Button,
  Badge,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHeader,
  TableHeaderCell,
  Tab,
  TabList,
  Dropdown,
  Option,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  ChevronLeftRegular,
  ChevronRightRegular,
  CalendarMonthRegular,
  CalendarWeekStartRegular,
  CalendarDayRegular,
  PersonRegular,
  BuildingRegular,
  DocumentTextRegular,
  ClockRegular,
  ListRegular,
} from '@fluentui/react-icons';
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addWeeks,
  addMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
  differenceInDays,
  getHours,
  getMinutes,
} from 'date-fns';
import { getTaxReturns } from '../services/api';
import { TableSkeleton } from '../components/LoadingSkeleton';
import DataTable, { type Column } from '../components/DataTable';
import { RowLink } from '../components/links/EntityLink';
import { realLinkProps } from '../components/links/realLinkProps';
import type { TaxReturn } from '../types';

const useStyles = makeStyles({
  statValueBrand: { color: tokens.colorBrandForeground1 },
  statValueSuccess: { color: tokens.colorPaletteGreenForeground1 },
  statValuePurple: { color: tokens.colorPaletteLavenderForeground2 },
  subtleText: { color: tokens.colorNeutralForeground2 },
  todayDateNumber: { color: tokens.colorBrandForeground1 },
  apptTimeBrand: { color: tokens.colorBrandForeground1 },
});

type ViewMode = 'month' | 'week' | 'day' | 'list';

interface AppointmentGroup {
  appointmentId: string;
  scheduledDate: Date;
  taxReturns: TaxReturn[];
}

const statusColors: Record<string, string> = {
  scheduled: '#0078d4',
  pending_resolution: '#8764b8',
  documents_pending: '#ca5010',
  in_preparation: '#107c10',
  in_review: '#008272',
  ready_for_signature: '#004e8c',
  waiting_for_payment: '#D97706',
  e_filed: '#4f6bed',
  completed: '#107c10',
  cancelled: 'var(--tl-text-tertiary)',
};

const statusDisplayNames: Record<string, string> = {
  scheduled: 'Scheduled',
  pending_resolution: 'Pending Resolution',
  documents_pending: 'Documents Pending',
  in_preparation: 'In Preparation',
  in_review: 'In Review',
  ready_for_signature: 'Ready for Signature',
  waiting_for_payment: 'Waiting for Payment',
  e_filed: 'E-Filed',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

// Generate time slots for day view (15-minute intervals)
const generateDayTimeSlots = () => {
  const slots: { hour: number; minute: number; label: string }[] = [];
  for (let hour = 7; hour < 20; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const time = new Date();
      time.setHours(hour, minute, 0, 0);
      slots.push({
        hour,
        minute,
        label: format(time, 'h:mm a'),
      });
    }
  }
  return slots;
};

// Generate time slots for week view (hourly intervals)
const generateWeekTimeSlots = () => {
  const slots: { hour: number; label: string }[] = [];
  for (let hour = 7; hour < 20; hour++) {
    const time = new Date();
    time.setHours(hour, 0, 0, 0);
    slots.push({
      hour,
      label: format(time, 'h a'),
    });
  }
  return slots;
};

const dayTimeSlots = generateDayTimeSlots();
const weekTimeSlots = generateWeekTimeSlots();

export default function CalendarPage() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { preparerId, preparerName, isLoading: userLoading } = useCurrentUser();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentGroup | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [dayOverviewDate, setDayOverviewDate] = useState<Date | null>(null);
  const [dayOverviewDialogOpen, setDayOverviewDialogOpen] = useState(false);
  const [selectedPreparer, setSelectedPreparer] = useState<string | undefined>();
  const [initialFilterSet, setInitialFilterSet] = useState(false);

  // Set default to current user when loaded
  useEffect(() => {
    if (!initialFilterSet && !userLoading) {
      if (preparerId) {
        setSelectedPreparer(preparerId);
      }
      setInitialFilterSet(true);
    }
  }, [preparerId, initialFilterSet, userLoading]);

  // Fetch all tax returns to extract preparer list
  const { data: allTaxReturns } = useQuery<TaxReturn[]>({
    queryKey: ['taxReturns', 'all-preparers'],
    queryFn: () => getTaxReturns({}),
    enabled: !userLoading,
  });

  // Extract unique preparers for the dropdown
  const availablePreparers = useMemo(() => {
    return allTaxReturns
      ?.filter(tr => tr.preparerId && tr.preparerName?.trim())
      .reduce((acc, tr) => {
        if (!acc.some(p => p.id === tr.preparerId)) {
          acc.push({ id: tr.preparerId!, name: tr.preparerName!.trim() });
        }
        return acc;
      }, [] as { id: string; name: string }[])
      .sort((a, b) => a.name.localeCompare(b.name)) || [];
  }, [allTaxReturns]);

  // Fetch tax returns - filter by selected preparer
  const { data: taxReturns, isLoading, isError, refetch } = useQuery<TaxReturn[]>({
    queryKey: ['taxReturns', selectedPreparer],
    queryFn: () => getTaxReturns({
      preparerId: selectedPreparer,
    }),
    enabled: !userLoading && initialFilterSet,
  });

  // Group tax returns by appointment (excluding inactive returns)
  const appointments = useMemo(() => {
    if (!taxReturns) return [];

    const grouped = new Map<string, AppointmentGroup>();

    taxReturns.forEach((tr) => {
      // Skip inactive returns in calendar view
      if (!tr.scheduledDate || !tr.appointmentId || tr.status === 'inactive') return;

      const key = tr.appointmentId;
      const date = parseISO(tr.scheduledDate);

      if (!grouped.has(key)) {
        grouped.set(key, {
          appointmentId: key,
          scheduledDate: date,
          taxReturns: [],
        });
      }
      grouped.get(key)!.taxReturns.push(tr);
    });

    return Array.from(grouped.values()).sort(
      (a, b) => a.scheduledDate.getTime() - b.scheduledDate.getTime()
    );
  }, [taxReturns]);

  // List view data - flatten tax returns with appointment info (excluding inactive)
  const listData = useMemo(() => {
    if (!taxReturns) return [];
    return taxReturns
      .filter(tr => tr.scheduledDate && tr.status !== 'inactive')
      .sort((a, b) => {
        const dateA = parseISO(a.scheduledDate);
        const dateB = parseISO(b.scheduledDate);
        return dateA.getTime() - dateB.getTime();
      });
  }, [taxReturns]);

  // Get appointments for a specific date
  const getAppointmentsForDate = (date: Date) => {
    return appointments.filter((appt) => isSameDay(appt.scheduledDate, date));
  };

  // Get appointments for a specific time slot on a date (day view - 15 min)
  const getAppointmentsForTimeSlot = (date: Date, hour: number, minute: number) => {
    return appointments.filter((appt) => {
      const apptHour = getHours(appt.scheduledDate);
      const apptMinute = getMinutes(appt.scheduledDate);
      return isSameDay(appt.scheduledDate, date) &&
             apptHour === hour &&
             apptMinute >= minute &&
             apptMinute < minute + 15;
    });
  };

  // Get appointments for a specific hour on a date (week view)
  const getAppointmentsForHour = (date: Date, hour: number) => {
    return appointments.filter((appt) => {
      const apptHour = getHours(appt.scheduledDate);
      return isSameDay(appt.scheduledDate, date) && apptHour === hour;
    });
  };

  // Navigation functions
  const goToToday = () => setCurrentDate(new Date());

  const goToPrevious = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, -1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, -1));
    } else if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, -1));
    }
  };

  const goToNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1));
    } else if (viewMode === 'week') {
      setCurrentDate(addWeeks(currentDate, 1));
    } else if (viewMode === 'day') {
      setCurrentDate(addDays(currentDate, 1));
    }
  };

  // Generate calendar days for month view
  const getMonthDays = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const days: Date[] = [];
    let day = startDate;

    while (day <= endDate) {
      days.push(day);
      day = addDays(day, 1);
    }

    return days;
  };

  // Generate days for week view
  const getWeekDays = () => {
    const weekStart = startOfWeek(currentDate);
    const days: Date[] = [];

    for (let i = 0; i < 7; i++) {
      days.push(addDays(weekStart, i));
    }

    return days;
  };

  // Handle appointment click
  const handleAppointmentClick = (appt: AppointmentGroup) => {
    setSelectedAppointment(appt);
    setDetailDialogOpen(true);
  };


  // Get title based on view mode
  const getTitle = () => {
    if (viewMode === 'month' || viewMode === 'list') {
      return format(currentDate, 'MMMM yyyy');
    } else if (viewMode === 'week') {
      const weekStart = startOfWeek(currentDate);
      const weekEnd = endOfWeek(currentDate);
      return `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`;
    } else {
      return format(currentDate, 'EEEE, MMMM d, yyyy');
    }
  };

  // Render appointment badge for month view
  const renderAppointmentBadge = (appt: AppointmentGroup, compact = false) => {
    const primaryReturn = appt.taxReturns[0];
    const hasMultiple = appt.taxReturns.length > 1;

    return (
      <div
        key={appt.appointmentId}
        onClick={(e) => { e.stopPropagation(); handleAppointmentClick(appt); }}
        style={{
          backgroundColor: statusColors[primaryReturn.status] || tokens.colorBrandBackground,
          color: 'white',
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: compact ? '11px' : '12px',
          cursor: 'pointer',
          marginBottom: '4px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={`${format(appt.scheduledDate, 'h:mm a')} - ${primaryReturn.clientName}${hasMultiple ? ` (+${appt.taxReturns.length - 1})` : ''}`}
      >
        {compact ? (
          primaryReturn.clientName.split(' ')[0]
        ) : (
          <>
            {format(appt.scheduledDate, 'h:mm a')} - {primaryReturn.clientName}
            {hasMultiple && <span style={{ opacity: 0.8 }}> (+{appt.taxReturns.length - 1})</span>}
          </>
        )}
      </div>
    );
  };

  // List view columns
  const listColumns: Column<TaxReturn>[] = [
    {
      key: 'scheduledDate',
      header: 'Date & Time',
      sortable: true,
      getValue: (item) => new Date(item.scheduledDate).getTime(),
      render: (item) => (
        <div>
          <div style={{ fontWeight: 500 }}>{format(parseISO(item.scheduledDate), 'MMM d, yyyy')}</div>
          <div style={{ fontSize: tokens.fontSizeBase200 }} className={styles.subtleText}>{format(parseISO(item.scheduledDate), 'h:mm a')}</div>
        </div>
      ),
    },
    {
      key: 'clientName',
      header: 'Client',
      sortable: true,
      render: (item) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {item.returnType === 'business' ? <BuildingRegular /> : <PersonRegular />}
          <div>
            <div style={{ fontWeight: 500 }}>
              <RowLink href={`/tax-returns/${item.id}`}>{item.clientName}</RowLink>
            </div>
            {item.spouseName && <div style={{ fontSize: tokens.fontSizeBase200 }} className={styles.subtleText}>& {item.spouseName}</div>}
          </div>
        </div>
      ),
    },
    {
      key: 'appointmentType',
      header: 'Type',
      sortable: true,
      render: (item) => <Badge appearance="outline">{item.appointmentType}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: Object.entries(statusDisplayNames).map(([value, label]) => ({ value, label })),
      render: (item) => (
        <Badge appearance="filled" style={{ backgroundColor: statusColors[item.status] || tokens.colorBrandBackground }}>
          {statusDisplayNames[item.status] || item.status}
        </Badge>
      ),
    },
    {
      key: 'preparerName',
      header: 'Preparer',
      sortable: true,
      render: (item) => item.preparerName || '-',
    },
  ];

  // Stats
  const todayAppointments = getAppointmentsForDate(new Date());
  const thisWeekAppointments = appointments.filter(
    (appt) => differenceInDays(appt.scheduledDate, new Date()) >= 0 && differenceInDays(appt.scheduledDate, new Date()) < 7
  );

  if (isLoading) {
    return <TableSkeleton />;
  }

  if (isError) {
    return (
      <div className="error-container">
        <p>Failed to load calendar appointments. Please try again.</p>
        <Button onClick={() => refetch()}>Retry</Button>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Calendar</h2>
        <p>View scheduled appointments and tax return deadlines</p>
      </div>

      {/* Stats */}
      <div className="stats-grid" style={{ marginBottom: '24px' }}>
        <div className="stat-card">
          <span className={`stat-value ${styles.statValueBrand}`}>{todayAppointments.length}</span>
          <span className="stat-label">Today's Appointments</span>
        </div>
        <div className="stat-card">
          <span className={`stat-value ${styles.statValueSuccess}`}>{thisWeekAppointments.length}</span>
          <span className="stat-label">This Week</span>
        </div>
        <div className="stat-card">
          <span className={`stat-value ${styles.statValuePurple}`}>{appointments.length}</span>
          <span className="stat-label">Total Scheduled</span>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {viewMode !== 'list' && (
              <>
                <Button appearance="subtle" icon={<ChevronLeftRegular />} onClick={goToPrevious} />
                <h3 style={{ margin: 0, minWidth: '220px', textAlign: 'center' }}>{getTitle()}</h3>
                <Button appearance="subtle" icon={<ChevronRightRegular />} onClick={goToNext} />
                <Button appearance="secondary" onClick={goToToday}>Today</Button>
              </>
            )}
            {viewMode === 'list' && <h3 style={{ margin: 0 }}>All Appointments</h3>}
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <TabList
              selectedValue={viewMode}
              onTabSelect={(_, data) => setViewMode(data.value as ViewMode)}
            >
              <Tab value="day" icon={<CalendarDayRegular />}>Day</Tab>
              <Tab value="week" icon={<CalendarWeekStartRegular />}>Week</Tab>
              <Tab value="month" icon={<CalendarMonthRegular />}>Month</Tab>
              <Tab value="list" icon={<ListRegular />}>List</Tab>
            </TabList>

            <Dropdown
              placeholder="Preparer"
              selectedOptions={selectedPreparer ? [selectedPreparer] : []}
              value={
                selectedPreparer
                  ? availablePreparers.find(p => p.id === selectedPreparer)?.name ||
                    (selectedPreparer === preparerId ? `My Calendar (${preparerName?.split(' ')[0]})` : 'Unknown')
                  : 'All Preparers'
              }
              onOptionSelect={(_, data) => {
                setSelectedPreparer(data.optionValue || undefined);
              }}
              style={{ minWidth: '180px' }}
            >
              <Option text="All Preparers" value="">All Preparers</Option>
              {availablePreparers.map((preparer) => (
                <Option key={preparer.id} text={preparer.name} value={preparer.id}>
                  {preparer.name}{preparer.id === preparerId ? ' (Me)' : ''}
                </Option>
              ))}
            </Dropdown>
          </div>
        </div>
      </div>

      {/* Calendar Views */}
      <div className="card">
        {/* Day View - 15 minute blocks */}
        {viewMode === 'day' && (
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr' }}>
              {dayTimeSlots.map((slot, idx) => {
                const slotAppointments = getAppointmentsForTimeSlot(currentDate, slot.hour, slot.minute);
                const isQuarterHour = slot.minute === 0;

                return (
                  <div key={idx} style={{ display: 'contents' }}>
                    <div
                      className={styles.subtleText}
                      style={{
                        padding: '4px 8px',
                        fontSize: tokens.fontSizeBase200,
                        borderBottom: '1px solid var(--tl-border)',
                        backgroundColor: isQuarterHour ? 'var(--tl-bg-page)' : 'white',
                        fontWeight: isQuarterHour ? 500 : 400,
                      }}
                    >
                      {isQuarterHour ? slot.label : ''}
                    </div>
                    <div
                      style={{
                        minHeight: '30px',
                        padding: '4px 8px',
                        borderBottom: '1px solid var(--tl-border)',
                        borderLeft: '1px solid var(--tl-border)',
                        backgroundColor: isQuarterHour ? 'var(--tl-bg-page)' : 'white',
                      }}
                    >
                      {slotAppointments.map((appt) => (
                        <div
                          key={appt.appointmentId}
                          onClick={() => handleAppointmentClick(appt)}
                          style={{
                            backgroundColor: statusColors[appt.taxReturns[0].status] || tokens.colorBrandBackground,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: tokens.fontSizeBase200,
                            cursor: 'pointer',
                          }}
                        >
                          {appt.taxReturns[0].clientName}
                          {appt.taxReturns.length > 1 && ` (+${appt.taxReturns.length - 1})`}
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Week View - Hourly blocks */}
        {viewMode === 'week' && (
          <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
            {/* Header */}
            <div style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)', position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
              <div style={{ padding: '12px', backgroundColor: 'var(--tl-bg-subtle)' }}></div>
              {getWeekDays().map((day) => (
                <div
                  key={day.toISOString()}
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    fontWeight: 600,
                    backgroundColor: isToday(day) ? '#e7f3ff' : 'var(--tl-bg-subtle)',
                    borderLeft: '1px solid var(--tl-border)',
                  }}
                >
                  <div>{format(day, 'EEE')}</div>
                  <div style={{ fontSize: tokens.fontSizeBase500 }} className={isToday(day) ? styles.todayDateNumber : undefined}>
                    {format(day, 'd')}
                  </div>
                </div>
              ))}
            </div>

            {/* Time slots */}
            {weekTimeSlots.map((slot) => (
              <div key={slot.hour} style={{ display: 'grid', gridTemplateColumns: '60px repeat(7, 1fr)' }}>
                <div
                  className={styles.subtleText}
                  style={{
                    padding: '8px',
                    fontSize: tokens.fontSizeBase200,
                    borderBottom: '1px solid var(--tl-border)',
                    backgroundColor: 'var(--tl-bg-page)',
                  }}
                >
                  {slot.label}
                </div>
                {getWeekDays().map((day) => {
                  const hourAppointments = getAppointmentsForHour(day, slot.hour);

                  return (
                    <div
                      key={day.toISOString()}
                      style={{
                        minHeight: '50px',
                        padding: '4px',
                        borderBottom: '1px solid var(--tl-border)',
                        borderLeft: '1px solid var(--tl-border)',
                        backgroundColor: isToday(day) ? '#f0f7ff' : 'white',
                      }}
                    >
                      {hourAppointments.map((appt) => (
                        <div
                          key={appt.appointmentId}
                          onClick={() => handleAppointmentClick(appt)}
                          style={{
                            backgroundColor: statusColors[appt.taxReturns[0].status] || tokens.colorBrandBackground,
                            color: 'white',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            marginBottom: '4px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                          title={`${format(appt.scheduledDate, 'h:mm a')} - ${appt.taxReturns[0].clientName}`}
                        >
                          {format(appt.scheduledDate, 'h:mm')} {appt.taxReturns[0].clientName.split(' ')[0]}
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}

        {/* Month View */}
        {viewMode === 'month' && (
          <div>
            {/* Weekday headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid var(--tl-border)' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <div
                  key={day}
                  style={{
                    padding: '12px',
                    textAlign: 'center',
                    fontWeight: 600,
                    backgroundColor: 'var(--tl-bg-subtle)',
                  }}
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar days */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
              {getMonthDays().map((day) => {
                const dayAppointments = getAppointmentsForDate(day);
                const isCurrentMonth = isSameMonth(day, currentDate);
                const isDayToday = isToday(day);

                return (
                  <div
                    key={day.toISOString()}
                    style={{
                      minHeight: '100px',
                      padding: '8px',
                      borderRight: '1px solid var(--tl-border)',
                      borderBottom: '1px solid var(--tl-border)',
                      backgroundColor: !isCurrentMonth ? 'var(--tl-bg-page)' : isDayToday ? '#e7f3ff' : 'white',
                    }}
                  >
                    <div
                      className={!isCurrentMonth ? styles.subtleText : isDayToday ? styles.todayDateNumber : undefined}
                      style={{
                        fontWeight: isDayToday ? 700 : 400,
                        marginBottom: '4px',
                      }}
                    >
                      {format(day, 'd')}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {dayAppointments.slice(0, 3).map((appt) => renderAppointmentBadge(appt, true))}
                      {dayAppointments.length > 3 && (
                        <div
                          className={styles.statValueBrand}
                          style={{
                            fontSize: '11px',
                            paddingLeft: '4px',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setDayOverviewDate(day);
                            setDayOverviewDialogOpen(true);
                          }}
                        >
                          +{dayAppointments.length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <DataTable
            data={listData}
            columns={listColumns}
            rowKey="id"
            searchable
            searchPlaceholder="Search appointments..."
            searchKeys={['clientName', 'preparerName']}
            emptyMessage="No appointments scheduled."
            getRowHref={(item) => `/tax-returns/${item.id}`}
          />
        )}
      </div>

      {/* Appointment Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={(_, data) => setDetailDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '600px' }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <ClockRegular />
                Appointment Details
              </div>
            </DialogTitle>
            <DialogContent>
              {selectedAppointment && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {/* Time and Date */}
                  <div style={{ padding: '12px', backgroundColor: 'var(--tl-bg-subtle)', borderRadius: '4px' }}>
                    <div style={{ fontWeight: 600, marginBottom: '4px' }}>
                      {format(selectedAppointment.scheduledDate, 'EEEE, MMMM d, yyyy')}
                    </div>
                    <div className={styles.apptTimeBrand} style={{ fontWeight: 500 }}>
                      {format(selectedAppointment.scheduledDate, 'h:mm a')}
                    </div>
                  </div>

                  {/* Tax Returns */}
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <DocumentTextRegular />
                      Tax Returns ({selectedAppointment.taxReturns.length})
                    </div>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHeaderCell>Client</TableHeaderCell>
                          <TableHeaderCell>Type</TableHeaderCell>
                          <TableHeaderCell>Status</TableHeaderCell>
                          <TableHeaderCell>Action</TableHeaderCell>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedAppointment.taxReturns.map((tr) => (
                          <TableRow key={tr.id}>
                            <TableCell>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {tr.returnType === 'business' ? <BuildingRegular /> : <PersonRegular />}
                                <div>
                                  <div style={{ fontWeight: 500 }}>{tr.clientName}</div>
                                  {tr.spouseName && (
                                    <div style={{ fontSize: tokens.fontSizeBase200 }} className={styles.subtleText}>
                                      & {tr.spouseName}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge appearance="outline">
                                {tr.appointmentType}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge
                                appearance="filled"
                                style={{ backgroundColor: statusColors[tr.status] || tokens.colorBrandBackground }}
                              >
                                {tr.status.replace(/_/g, ' ')}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Button
                                as="a"
                                appearance="subtle"
                                size="small"
                                {...realLinkProps(`/tax-returns/${tr.id}`, {
                                  navigate,
                                  onFollow: () => setDetailDialogOpen(false),
                                })}
                              >
                                View
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDetailDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Day Overview Dialog */}
      <Dialog open={dayOverviewDialogOpen} onOpenChange={(_, data) => setDayOverviewDialogOpen(data.open)}>
        <DialogSurface style={{ maxWidth: '500px' }}>
          <DialogBody>
            <DialogTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <CalendarDayRegular />
                {dayOverviewDate && format(dayOverviewDate, 'EEEE, MMMM d, yyyy')}
              </div>
            </DialogTitle>
            <DialogContent>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '400px', overflowY: 'auto' }}>
                {dayOverviewDate && getAppointmentsForDate(dayOverviewDate).map((appt) => (
                  <div
                    key={appt.appointmentId}
                    style={{
                      padding: '12px',
                      borderRadius: '4px',
                      backgroundColor: 'var(--tl-bg-subtle)',
                      cursor: 'pointer',
                    }}
                    onClick={() => {
                      setSelectedAppointment(appt);
                      setDayOverviewDialogOpen(false);
                      setDetailDialogOpen(true);
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600 }}>
                        {format(appt.scheduledDate, 'h:mm a')}
                      </span>
                      <Badge appearance="filled" color="informative">
                        {appt.taxReturns.length} return{appt.taxReturns.length > 1 ? 's' : ''}
                      </Badge>
                    </div>
                    <div style={{ marginTop: '4px' }} className={styles.subtleText}>
                      {appt.taxReturns.map(tr => tr.clientName).join(', ')}
                    </div>
                  </div>
                ))}
              </div>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setDayOverviewDialogOpen(false)}>
                Close
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
